import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useClient } from '@/hooks/use-client';
import { useAuthStore } from '@/stores/auth';
import { useInstance } from '@/stores/instance';

import { useScopeUrl } from '../use-scope-url';

import type { PlApiClient, StreamingError, StreamingEvent, StreamingParams } from 'pl-api';

const useTimelineStream = (
  stream: string,
  params: StreamingParams = {},
  enabled = true,
  listener?: (event: StreamingEvent) => any,
) => {
  const client = useClient();
  const scopeUrl = useScopeUrl();
  // Subscribe to metadata updates; the SDK owns the current transport endpoint.
  useInstance();
  const authToken = useAuthStore((state) =>
    scopeUrl ? state.users[scopeUrl]?.access_token : undefined,
  );
  const token = client.accessToken;
  const software = client.features.version.software;
  const streamingUrl = client.instanceInformation.configuration.urls.streaming;
  const { list, tag, group, instance: remoteInstance } = params;
  const key = JSON.stringify([
    scopeUrl,
    token,
    software,
    streamingUrl,
    stream,
    list,
    tag,
    group,
    remoteInstance,
  ]);
  const latestListener = useRef(listener);
  const cleanupRef = useRef<(() => void) | undefined>(undefined);
  const [state, setState] = useState<{
    client: PlApiClient;
    key: string;
    connected: boolean;
    updatesAvailable: boolean;
    error?: StreamingError;
  }>();

  // Update only committed listeners, without releasing/reacquiring their subscriptions.
  useLayoutEffect(() => {
    latestListener.current = listener;
  }, [listener]);
  const forward = useCallback((event: StreamingEvent) => latestListener.current?.(event), []);

  useEffect(() => {
    if (!enabled || !stream || !streamingUrl) return;
    const session = client.streaming.connect();
    const leaseKey = JSON.stringify([
      scopeUrl,
      token,
      software,
      streamingUrl,
      stream,
      list,
      tag,
      group,
      remoteInstance,
    ]);
    let active = true;
    const subscriptionParams = { list, tag, group, instance: remoteInstance };
    const update = () => {
      if (active)
        setState({
          client,
          key: leaseKey,
          connected: session.connected,
          updatesAvailable: session.canUseUpdates(stream, subscriptionParams),
          error: session.getError(stream, subscriptionParams),
        });
    };
    session.listen(forward);
    const unwatch = session.onStateChange(update);
    const unwatchAvailability = session.onAvailabilityChange(update);
    const release = session.acquire(stream, { list, tag, group, instance: remoteInstance });
    const cleanup = () => {
      if (!active) return;
      active = false;
      unwatch();
      unwatchAvailability();
      session.unlisten(forward);
      release();
    };
    cleanupRef.current = cleanup;
    return () => {
      cleanup();
      if (cleanupRef.current === cleanup) cleanupRef.current = undefined;
    };
  }, [
    client,
    scopeUrl,
    token,
    software,
    streamingUrl,
    stream,
    list,
    tag,
    group,
    remoteInstance,
    enabled,
    forward,
    authToken,
  ]);

  const disconnect = useCallback(() => {
    cleanupRef.current?.();
    setState(undefined);
  }, []);
  const current = enabled && state?.client === client && state.key === key;
  return {
    disconnect,
    connected: !!(current && state.connected),
    updatesAvailable: !!(current && state.updatesAvailable),
    error: current ? state.error : undefined,
  };
};

export { useTimelineStream };
